import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAppConfig } from "@/lib/useAppConfig";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo } from "@/components/AppShell";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-stone-700">{label}</span>
    <input {...props} data-testid={testid}
      className="mt-1.5 w-full h-12 px-4 rounded-xl border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#C86D51] focus:border-transparent" />
  </label>
);

const Check = ({ checked, onChange, testid, children }) => (
  <label className="flex gap-3 text-sm items-start cursor-pointer text-stone-700">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5 h-5 w-5 shrink-0 accent-[#2D2926]" />
    <span>{children}</span>
  </label>
);

function SignupConsents({ form, setForm }) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-3 pt-1" data-testid="register-consents">
      <div>
        <p className="text-sm font-medium text-stone-700 mb-2">Where do you live?</p>
        <div className="grid grid-cols-3 gap-2" data-testid="register-country">
          {[["CA", "Canada"], ["US", "United States"], ["OTHER", "Elsewhere"]].map(([v, l]) => (
            <button type="button" key={v} onClick={() => set("country")(v)} aria-pressed={form.country === v} data-testid={`register-country-${v.toLowerCase()}`}
              className={`min-h-11 rounded-xl border px-2 text-sm font-medium ${form.country === v ? "border-[#2B2622] bg-[#2B2622] text-white" : "border-stone-300 bg-white"}`}>{l}</button>
          ))}
        </div>
        {form.country === "CA" && <ProvinceField value={form.province} onChange={set("province")} />}
        {form.country === "OTHER" && <p className="text-xs text-stone-600 mt-2" data-testid="register-country-unavailable">CAMRYN is only available in Canada and the United States for now. <Link to="/waitlist" className="tap underline">Join the waitlist</Link> and we'll let you know when it reaches you.</p>}
      </div>
      <Check checked={form.age_confirmed} onChange={set("age_confirmed")} testid="register-age-checkbox">I'm 18 or older.</Check>
      <Check checked={form.data_consent} onChange={set("data_consent")} testid="register-data-consent-checkbox">
        I agree to the <Link to="/legal/terms" target="_blank" className="underline">Terms</Link> and consent to CAMRYN storing my wellness data (check-ins, cycle, weight and notes) as described in the <Link to="/legal/health-data" target="_blank" className="underline">Health Data Policy</Link> and <Link to="/legal/privacy" target="_blank" className="underline">Privacy Policy</Link>.
      </Check>
      <p className="text-xs text-stone-500">AI coaching is a separate choice you'll make next. It's off until you turn it on. <Link to="/privacy-promise" target="_blank" data-testid="register-privacy-promise-link" className="tap underline">How CAMRYN protects you</Link></p>
    </div>
  );
}

const PROVINCES = [["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"], ["NB", "New Brunswick"], ["NL", "Newfoundland and Labrador"],
  ["NS", "Nova Scotia"], ["NT", "Northwest Territories"], ["NU", "Nunavut"], ["ON", "Ontario"], ["PE", "Prince Edward Island"], ["QC", "Quebec"],
  ["SK", "Saskatchewan"], ["YT", "Yukon"]];

function ProvinceField({ value, onChange }) {
  return (
    <div className="mt-3">
      <label className="block"><span className="text-sm font-medium text-stone-700">Province or territory</span>
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="register-province-select"
          className="mt-1.5 w-full h-12 px-4 rounded-xl border border-stone-300 bg-white text-base">
          <option value="">Choose…</option>
          {PROVINCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select></label>
      {value === "QC" && <p className="text-xs text-stone-600 mt-2" data-testid="register-quebec-unavailable">CAMRYN isn't available in Quebec yet. We're finishing a privacy review first. <Link to="/waitlist" className="tap underline">Join the waitlist</Link> and we'll tell you when it opens.</p>}
    </div>
  );
}

function InviteField({ value, onChange }) {
  return (
    <div data-testid="register-invite-block">
      <Field label="Invite code" testid="register-invite-input" value={value} onChange={onChange} placeholder="CAM-XXXX-XXXX" autoComplete="off" />
      <p className="text-xs text-stone-500 mt-1.5">CAMRYN is invite-only for now. No code? <Link to="/waitlist" data-testid="register-waitlist-link" className="tap text-[#C86D51] font-semibold">Join the waitlist</Link></p>
    </div>
  );
}

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { invite_only } = useAppConfig();
  const [form, setForm] = useState({ name: "", email: "", password: "", age_confirmed: false, data_consent: false, country: "", province: "", invite_code: params.get("invite") || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const next = params.get("next")?.startsWith("/") && !params.get("next").startsWith("//") ? params.get("next") : null;
  if (user) return <Navigate to={user.onboarded ? next || "/today" : "/start"} replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = isLogin ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password, { age_confirmed: form.age_confirmed, data_consent: form.data_consent, invite_code: form.invite_code || null, country: form.country, province: form.country === "CA" ? form.province : null });
      nav(u.onboarded ? next || "/today" : "/start");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="/images/feet-in-water.jpg" alt="Bare feet dipped in a calm lake beside a mug of coffee" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D2926]/70 to-transparent" />
        <p className="absolute bottom-12 left-12 right-12 font-display text-3xl text-white leading-snug">"The point isn't more data. It's knowing what to do with today."</p>
      </div>
      <div className="relative z-10 flex flex-col justify-center px-6 py-12 sm:px-16">
        <Link to="/" className="mb-12"><Logo /></Link>
        <h1 className="font-display text-3xl sm:text-4xl text-stone-900 tracking-tight">{isLogin ? "Welcome back" : "Create your space"}</h1>
        <p className="text-stone-600 mt-2 mb-8">{isLogin ? "Pick up where you left off." : "Free to start. Takes about two minutes."}</p>
        <form onSubmit={submit} className="space-y-4 max-w-sm" data-testid={`${mode}-form`}>
          {!isLogin && invite_only && <InviteField value={form.invite_code} onChange={set("invite_code")} />}
          {!isLogin && <Field label="First name" testid="register-name-input" value={form.name} onChange={set("name")} required />}
          <Field label="Email" type="email" testid={`${mode}-email-input`} value={form.email} onChange={set("email")} required autoComplete="email" />
          <Field label="Password" type="password" testid={`${mode}-password-input`} value={form.password} onChange={set("password")} required minLength={isLogin ? 1 : 8}
            autoComplete={isLogin ? "current-password" : "new-password"} placeholder={isLogin ? "" : "At least 8 characters"} />
          {isLogin && <Link to="/forgot-password" data-testid="login-forgot-link" className="tap block text-sm text-stone-600 hover:text-stone-900">Forgot password?</Link>}
          {!isLogin && <SignupConsents form={form} setForm={setForm} />}
          {error && <p data-testid={`${mode}-error`} className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          <button disabled={busy || (!isLogin && !(form.age_confirmed && form.data_consent && (form.country === "US" || (form.country === "CA" && form.province && form.province !== "QC"))))} data-testid={`${mode}-submit-btn`} className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-60">
            {busy ? "One moment…" : isLogin ? "Sign in" : "Create account"}
          </button>
        </form>
        <p className="text-sm text-stone-600 mt-6">
          {isLogin ? "New to CAMRYN? " : "Already have an account? "}
          <Link to={isLogin ? "/register" : "/login"} data-testid="auth-switch-link" className="tap text-[#C86D51] font-semibold">{isLogin ? "Create an account" : "Sign in"}</Link>
        </p>
      </div>
    </div>
  );
}
